import React, { useState, useEffect } from 'react';
import Select from 'react-select';
import CSVReader from 'react-csv-reader';
import axios from 'axios';

const UploadTask = () => {
  const [servers, setServers] = useState([]);
  const [selectedServer, setSelectedServer] = useState(null);
  const [taskType, setTaskType] = useState('order'); // Default: Order
  const [uploadMessage, setUploadMessage] = useState('');
  const [customerList, setCustomerList] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const customersPerPage = 5; // ✅ Show 5 customers per page
  
  // ✅ Filter customers based on selected serverName
  const filteredCustomers = customerList.filter(customer => customer.serverName === selectedServer);

  // ✅ Pagination Logic
  const totalPages = Math.ceil(filteredCustomers.length / customersPerPage);
  const indexOfLastCustomer = currentPage * customersPerPage;
  const indexOfFirstCustomer = indexOfLastCustomer - customersPerPage;
  const currentCustomers = filteredCustomers.slice(indexOfFirstCustomer, indexOfLastCustomer);

  // ✅ Fetch Servers on Load
  useEffect(() => {
    const fetchServers = async () => {
      try {
        const response = await axios.get("https://astrovidhya.in/api/servers");
        if (response.data && Array.isArray(response.data)) {
          setServers(response.data.map(server => ({
            _id: server._id,
            serverName: server.serverName // ✅ Store & Display `serverName`
          })));
        }
      } catch (error) {
        console.error("Error fetching servers:", error);
      }
    };
    fetchServers();
  }, []);

  // ✅ Fetch Customers Based on Selected Server
  const fetchCustomers = async (serverName) => {
    try {
      const response = await axios.get(`https://astrovidhya.in/api/customers?serverName=${serverName}`);
      setCustomerList(response.data.customers);
    } catch (error) {
      console.error('Error fetching customers:', error);
    }
  };

  // ✅ Handle Server Selection
  const handleServerChange = (event) => {
    const selectedServerName = event.target.value;
    setSelectedServer(selectedServerName);
    fetchCustomers(selectedServerName); // ✅ Refresh customers on dropdown change
  };

  // ✅ Handle File Upload
  const handleFileUpload = async (data) => {
    if (!selectedServer) {
      setUploadMessage("❌ Please select a server before uploading.");
      return;
    }
  
    try {
      await axios.post("https://astrovidhya.in/api/upload-customers", {
        data, // ✅ Send only CSV data
        taskType, // ✅ Send selected task type
        selectedServer, // ✅ Send selected server **NAME** (not ID)
      });
  
      setUploadMessage("✅ File uploaded successfully!");
  
      // ✅ Auto-refresh customer list after upload
      setTimeout(() => {
        fetchCustomers(selectedServer);
      }, 1000);
  
    } catch (error) {
      console.error("❌ Error uploading file:", error.response?.data || error.message);
      setUploadMessage("❌ Failed to upload data.");
    }
  };
  

  return (
    <div className="container mx-auto p-4">
      {/* ✅ Page Title */}
      <h2 className="text-2xl font-bold mb-4">Upload Task</h2>

      {/* ✅ Task Type Selection */}
      <div className="mb-4">
        <label className="block text-gray-700 font-bold mb-2">Task Type</label>
        <div className="flex items-center gap-4">
          <label>
            <input
              type="radio"
              name="taskType"
              value="order"
              checked={taskType === 'order'}
              onChange={(e) => setTaskType(e.target.value)}
            />
            Order
          </label>
          <label>
            <input
              type="radio"
              name="taskType"
              value="subscription"
              checked={taskType === 'subscription'}
              onChange={(e) => setTaskType(e.target.value)}
            />
            Subscription
          </label>
        </div>
      </div>

      {/* ✅ Server Selection Dropdown */}
      <div className="mb-4">
        <label className="block text-gray-700 text-sm font-bold mb-2">Select Server</label>
        <select value={selectedServer} onChange={handleServerChange}>
          <option value="">Select a Server</option>
          {servers.map((server) => (
            <option key={server.serverName} value={server.serverName}> 
              {server.serverName} {/* ✅ Display serverName */}
            </option>
          ))}
        </select>
      </div>

      {/* ✅ CSV Upload Button */}
      <div className="mb-4">
        <label className="block text-gray-700 text-sm font-bold mb-2">Upload CSV File</label>
        <CSVReader onFileLoaded={handleFileUpload} parserOptions={{ header: true }} />
      </div>

      {/* ✅ Upload Status Message */}
      {uploadMessage && (
        <p className={`mt-2 text-sm ${uploadMessage.includes("✅") ? "text-green-500" : "text-red-500"}`}>
          {uploadMessage}
        </p>
      )}

      {/* ✅ Customers List Table */}
      <div className="mt-4">
        <h3 className="text-xl font-bold mb-4">Customer List ({filteredCustomers.length})</h3>
        <table className="table-auto border-collapse border border-gray-300 w-full text-sm">
          <thead>
            <tr>
              <th className="border border-gray-300 px-4 py-2">Date</th>
              <th className="border border-gray-300 px-4 py-2">Name</th>
              <th className="border border-gray-300 px-4 py-2">Address</th>
              <th className="border border-gray-300 px-4 py-2">City</th>
              <th className="border border-gray-300 px-4 py-2">State</th>
              <th className="border border-gray-300 px-4 py-2">Mobile</th>
              <th className="border border-gray-300 px-4 py-2">Pincode</th>
              <th className="border border-gray-300 px-4 py-2">Product</th>
              <th className="border border-gray-300 px-4 py-2">Pay Mode</th>
            </tr>
          </thead>
          <tbody>
            {currentCustomers.map((customer, index) => (
              <tr key={index}>
                <td className="border border-gray-300 px-4 py-2">{customer.date}</td>
                <td className="border border-gray-300 px-4 py-2">{customer.name}</td>
                <td className="border border-gray-300 px-4 py-2">{customer.address}</td>
                <td className="border border-gray-300 px-4 py-2">{customer.city}</td>
                <td className="border border-gray-300 px-4 py-2">{customer.state}</td>
                <td className="border border-gray-300 px-4 py-2">{customer.mobile}</td>
                <td className="border border-gray-300 px-4 py-2">{customer.pincode}</td>
                <td className="border border-gray-300 px-4 py-2">{customer.product}</td>
                <td className="border border-gray-300 px-4 py-2">{customer.payMode}</td>
              </tr>
            ))}
          </tbody>
        </table>

        {/* ✅ Pagination Controls */}
        <div className="flex justify-center items-center mt-4">
          <button onClick={() => setCurrentPage((p) => Math.max(p - 1, 1))}>⬅️ Previous</button>
          <span>Page {currentPage} of {totalPages}</span>
          <button onClick={() => setCurrentPage((p) => Math.min(p + 1, totalPages))}>Next ➡️</button>
        </div>
      </div>
    </div>
  );
};

export default UploadTask;
